import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useNavigate } from 'react-router-dom';

import cs from 'classnames';
import html2canvas from 'html2canvas';
import html2pdf from 'html2pdf.js';
import { useRecoilValue } from 'recoil';

import { getClinicalNote } from 'api/ClinicalNote';
import { getMedications } from 'api/Medication';

import { ia } from 'lib/helpers/utility';

import PracticeDetailsHeader from 'components/practice/charts/ClinicalNote/Orders/views/LabOrderPreview/components/PracticeDetailsHeader';
import { getOverview } from 'components/practice/charts/ClinicalNote/lib/getOverview';
import { getCustomFormResponse } from 'components/practice/charts/ClinicalNote/lib/getPatientHistory';
import { currentPractice } from 'components/practice/practiceState';
import { showAlert } from 'components/shared/Alert/Alert';
import Skeleton from 'components/shared/Skeleton/Skeleton';
import { userState } from 'components/state';

import {
  buildSelectionObject,
  fetchAdditionalNoteData,
  prepareComponentProps,
  processFormData
} from '../lib/ClinicalNoteData';

import PatientDetails from './ExportPDF/PatientDetails';
import SingleClinicalNotePDF from './ExportPDF/SingleClinicalNotePDF';

const ClinicalNotePDF = ({
  itemProps = [],
  renderedRef = [],
  contentId = '',
  downloadPDF = false,
  setButtonLoading = () => {},
  setSelectedRows = () => {}
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState([]);
  const navigate = useNavigate();

  const practice = useRecoilValue(currentPractice);
  const currentUser = useRecoilValue(userState);
  const cnDisplaySettings = currentUser?.display_settings?.clinicalNote;

  useEffect(() => {
    if (itemProps) {
      setLoading(true);
      prepareForms();
    }
  }, [itemProps]);

  const prepareForms = async () => {
    const cnData = itemProps;

    const newResult = await Promise.all(
      cnData.map(async (item) => {
        const appointmentId = item?.appointmentId;
        const patientId = item?.patient?.id;

        const result = await getClinicalNote(navigate, {
          appointmentId,
          loadedPracticeId: practice?.id
        });

        const currentHpOverviewData = await getOverview({
          patientId,
          appointmentId,
          navigate,
          isKebab: false
        });

        const overviewData = await getCustomFormResponse({
          patientId,
          type: [
            'allergies',
            'vaccines',
            'familyHistory',
            'surgicalHistory',
            'weightLossAdministration'
          ]
        });

        const data = await getMedications(() => {}, { patientId, v2: true });

        const additionalData = await fetchAdditionalNoteData(navigate, appointmentId, patientId);

        if (ia(data?.medicationHistory?.response?.medications)) {
          overviewData.medicationHistory = data?.medicationHistory?.response?.medications;
        }

        const processedData = processFormData(result, cnDisplaySettings);
        const selectedResult = await buildSelectionObject(
          processedData,
          additionalData,
          cnDisplaySettings,
          currentHpOverviewData,
          overviewData
        );

        const componentProps = prepareComponentProps(selectedResult, result?.clinicalNote);

        return {
          customClinicalNote: {
            clinicalNote: { ...processedData.clinicalNote, ...processedData.cpt_codes },
            advancedHP: processedData.advancedHP,
            advancedSOAP: processedData.advancedSOAP,
            currentHpOverviewData,
            overviewData,
            customFormTypes: processedData.customFormTypes
          },
          componentProps: { ...componentProps },
          appointmentDate: item?.startsAt,
          patient: item?.patient,
          provider: item?.provider
        };
      })
    );
    setResult(newResult);
    setLoading(false);
  };

  const generateAndSavePDF = async () => {
    let docIndex = 0;
    for (const rendered of renderedRef.current) {
      if (!rendered) {
        console.warn('Skipping PDF generation for null rendered element at index:', docIndex);
        docIndex++;
        continue;
      }
      await new Promise((resolve) => setTimeout(resolve, 500));

      const currentItem = itemProps[docIndex];
      const sideMargin = 10; // mm
      const bottomMargin = 15; // mm, for footer (page number)
      const headerContentWidthMm = 210 - 2 * sideMargin; // A4 width (210mm) - side margins

      // Helper function to render React components to an image
      const renderReactToImage = async (jsxContent, targetWidthMm) => {
        const container = document.createElement('div');
        container.style.width = `${targetWidthMm}mm`;
        document.body.appendChild(container);

        const root = createRoot(container);
        root.render(jsxContent);
        await new Promise((r) => setTimeout(r, 200)); // Allow time for rendering

        const elementToCapture = container.firstChild;
        if (!elementToCapture) {
          root.unmount();
          document.body.removeChild(container);
          throw new Error('Header component did not render a child element to capture.');
        }

        const canvas = await html2canvas(elementToCapture, {
          scale: 2,
          useCORS: true,
          backgroundColor: null,
          logging: false,
          width: elementToCapture.scrollWidth,
          height: elementToCapture.scrollHeight,
          windowWidth: elementToCapture.scrollWidth,
          windowHeight: elementToCapture.scrollHeight
        });

        root.unmount();
        document.body.removeChild(container);

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        let pdfHeight = 0;
        if (canvas.width > 0 && canvas.height > 0) {
          const aspectRatio = canvas.height / canvas.width;
          pdfHeight = targetWidthMm * aspectRatio;
        }
        return { imgData, pdfHeight, pdfWidth: targetWidthMm };
      };

      const firstPageHeaderJsx = (
        <div>
          <PracticeDetailsHeader practice={practice} />
          <PatientDetails
            patient={currentItem?.patient}
            appointmentDate={currentItem?.appointmentDate}
            timezone={practice?.timezone}
            practice={practice}
          />
        </div>
      );
      const firstPageHeader = await renderReactToImage(firstPageHeaderJsx, headerContentWidthMm);

      const subsequentPageHeaderJsx = (
        <PatientDetails
          patient={currentItem?.patient}
          appointmentDate={currentItem?.appointmentDate}
          timezone={practice?.timezone}
          practice={practice}
        />
      );
      const subsequentPageHeader = await renderReactToImage(
        subsequentPageHeaderJsx,
        headerContentWidthMm
      );

      const minTopMargin = 10; // mm
      const dynamicTopMargin =
        firstPageHeader.pdfHeight > 0 ? firstPageHeader.pdfHeight : minTopMargin;

      const opt = {
        margin: [dynamicTopMargin, sideMargin, bottomMargin, sideMargin],
        filename: `${currentItem?.startsAt}_${currentItem?.patient?.fullName}_clinical_notes.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      try {
        const pdfWorker = html2pdf().set(opt).from(rendered).toPdf();
        const pdf = await pdfWorker.get('pdf');
        const totalPages = pdf.internal.getNumberOfPages();

        const pdfPageWidth = pdf.internal.pageSize.getWidth();
        const pdfPageHeight = pdf.internal.pageSize.getHeight();
        const headerX = sideMargin;
        const headerY = 0; // Headers will be placed at the top of the page margin

        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);

          if (i === 1) {
            // Add First Page Header
            if (firstPageHeader.pdfHeight > 0) {
              pdf.addImage(
                firstPageHeader.imgData,
                'JPEG',
                headerX,
                headerY,
                firstPageHeader.pdfWidth,
                firstPageHeader.pdfHeight
              );
            }
          } else {
            // Add Subsequent Page Header
            if (subsequentPageHeader.pdfHeight > 0) {
              pdf.addImage(
                subsequentPageHeader.imgData,
                'JPEG',
                headerX,
                headerY + 5,
                subsequentPageHeader.pdfWidth,
                subsequentPageHeader.pdfHeight
              );
            }
          }

          pdf.setFontSize(10);
          pdf.setTextColor(0);
          const pageNumText = `${i}/${totalPages}`;
          const pageNumX = pdfPageWidth - sideMargin;
          const pageNumY = pdfPageHeight - bottomMargin / 2; // Center in bottom margin
          pdf.text(pageNumText, pageNumX, pageNumY, { align: 'right' });
        }

        await pdfWorker.save();
        setSelectedRows([]);
        setButtonLoading(false);
      } catch (error) {
        showAlert({ title: 'Error generating PDF:', message: error.message || String(error) });
        setButtonLoading(false);
        setSelectedRows([]);
      }
      docIndex++;
    }
  };

  const generatePDF = async () => {
    try {
      await generateAndSavePDF();
    } catch (error) {
      showAlert({ title: 'Error generating PDF:', message: error });
    }
  };

  useEffect(() => {
    if (
      downloadPDF &&
      renderedRef?.current?.length === result?.length &&
      renderedRef?.current?.every((ref) => ref !== null) &&
      !loading
    ) {
      generatePDF();
    }
  }, [result, renderedRef?.current, loading]);

  return (
    <>
      <div className={cs('p-3', downloadPDF && 'hidden')} id={contentId}>
        {loading ? (
          <Skeleton count={5} />
        ) : ia(result) ? (
          result.map((row, index) => {
            return (
              <div
                ref={(el) => (renderedRef.current[index] = el)}
                key={index}
                className="print:!page-break-before">
                <SingleClinicalNotePDF key={index} row={row} />
              </div>
            );
          })
        ) : (
          <p className="text-neutral-800">No clinical summary found</p>
        )}
      </div>
    </>
  );
};

export default ClinicalNotePDF;
